import { useState } from 'react';
import { toast } from 'sonner';

import { caseRecordService, summaryService } from '@/services/api';
import { sobService } from '@/services/sobService';

const MAX_BULK_LIMIT = 10; // Default maximum number of SOBs that can be downloaded at once

interface UseDownloadSOBOptions {
  maxBulkLimit?: number;
}

export const useDownloadSOB = (options?: UseDownloadSOBOptions) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const maxLimit = options?.maxBulkLimit ?? MAX_BULK_LIMIT;

  /**
   * Download a single SOB for a case
   */
  const downloadIndividualSOB = async (
    caseId: string,
    organizationId: string,
    programId: string,
  ): Promise<void> => {
    if (isDownloading) return;

    setIsDownloading(true);
    const toastId = toast.loading('Fetching case data...');

    try {
      // Fetch case data and summary in parallel
      const [caseData, summaryResponse] = await Promise.all([
        caseRecordService.getCaseRecord({
          organization_id: organizationId,
          program_id: programId,
          case_record_id: caseId,
        }),
        summaryService
          .getCaseSummary({
            organization_id: organizationId,
            program_id: programId,
            case_id: caseId,
          })
          .catch(() => null), // Summary is optional, don't fail if not available
      ]);

      // Extract section_summaries from summary response
      const summary = summaryResponse?.summary?.content?.section_summaries
        ? { section_summaries: summaryResponse.summary.content.section_summaries }
        : undefined;

      // Generate and download PDF
      toast.loading('Generating SOB PDF...', { id: toastId });
      await sobService.downloadSOB(caseData, programId, summary);

      toast.success('SOB downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Failed to download SOB:', error);
      toast.error('Failed to download SOB', { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Download multiple SOBs as a ZIP file
   */
  const downloadBulkSOB = async (
    caseIds: string[],
    organizationId: string,
    programId: string,
    programName: string,
  ): Promise<void> => {
    if (isDownloading) return;

    // Validate selection count
    if (caseIds.length === 0) {
      toast.error('Please select at least one case');
      return;
    }

    if (caseIds.length > maxLimit) {
      toast.error(
        `Cannot download more than ${maxLimit} SOBs at once. You have selected ${caseIds.length} cases.`,
      );
      return;
    }

    setIsDownloading(true);
    const toastId = toast.loading(
      `Fetching data for ${caseIds.length} case${caseIds.length > 1 ? 's' : ''}...`,
    );

    try {
      // Fetch all case data and summaries in parallel
      const casesData = await Promise.all(
        caseIds.map(async caseId => {
          try {
            // Fetch case data and summary in parallel for each case
            const [caseData, summaryResponse] = await Promise.all([
              caseRecordService.getCaseRecord({
                organization_id: organizationId,
                program_id: programId,
                case_record_id: caseId,
              }),
              summaryService
                .getCaseSummary({
                  organization_id: organizationId,
                  program_id: programId,
                  case_id: caseId,
                })
                .catch(() => null), // Summary is optional
            ]);

            // Attach summary to case data if available
            const summary = summaryResponse?.summary?.content?.section_summaries
              ? { section_summaries: summaryResponse.summary.content.section_summaries }
              : undefined;

            return {
              ...caseData,
              summary,
            };
          } catch (error) {
            console.error(`Failed to fetch case ${caseId}:`, error);
            // Return a minimal case object with error flag
            return {
              id: caseId,
              error: true,
            };
          }
        }),
      );

      // Filter out cases that failed to load
      const validCases = casesData.filter((caseData: any) => !caseData.error);

      if (validCases.length === 0) {
        throw new Error('Failed to fetch any case data');
      }

      if (validCases.length < casesData.length) {
        toast.warning(
          `${casesData.length - validCases.length} case(s) could not be fetched and will be skipped`,
          { duration: 5000 },
        );
      }

      // Generate and download PDFs as ZIP using Web Worker
      toast.loading('Generating SOB PDFs...', { id: toastId });

      await sobService.downloadBulkSOBWithWorker(
        validCases,
        programId,
        programName,
        (current, total, phase) => {
          const message =
            phase === 'generating'
              ? `Generating PDF ${current} of ${total}...`
              : `Compressing PDFs (${current}/${total})...`;
          toast.loading(message, { id: toastId });
        },
      );

      toast.success(
        `${validCases.length} SOB${validCases.length > 1 ? 's' : ''} downloaded successfully`,
        { id: toastId },
      );
    } catch (error) {
      console.error('Failed to download SOBs:', error);
      toast.error('Failed to download SOBs', { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    downloadIndividualSOB,
    downloadBulkSOB,
    isDownloading,
    maxBulkLimit: maxLimit,
  };
};
